
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchActivityLogs, logActivity, getClientIP } from '../../utils/activityLogApi';

// ─── Brand Design System ─────────────────────────────────────────────
const BRAND = {
  primary:        '#6BCE8B',
  primaryDark:    '#4EAD6E',
  primaryLight:   '#E8F8EE',
  secondary:      '#035EA5',
  secondaryDark:  '#024A82',
  secondaryLight: '#E6F0F9',
  white:          '#FFFFFF',
  gray50:         '#F8FAFC',
  gray100:        '#F1F5F9',
  gray200:        '#E2E8F0',
  gray300:        '#CBD5E1',
  gray400:        '#94A3B8',
  gray500:        '#64748B',
  gray600:        '#475569',
  gray900:        '#0F172A',
};

const FONT = "'Inter', sans-serif";

const MODULES = [
  'DPDP', 'GDPR', 'CCPA', 'UAE','RoPA',
  'Settings', 'ManageUsers', 'DAST',
  'Dashboard', 'Third-Party', 'Internal', 'Access Log','Nav','Global Compliance & Trust'
];

// ─── Layout/surface tokens ───────────────────────────────────────────
const T = {
  bg:          BRAND.gray50,
  card:        BRAND.white,
  border:      BRAND.gray200,
  borderLight: BRAND.gray100,
  text:        BRAND.gray900,
  textMuted:   BRAND.gray500,
  textFaint:   BRAND.gray400,
  brand:       BRAND.primary,
  brandHover:  BRAND.primaryDark,
  brandLight:  BRAND.primaryLight,
  brandMid:    '#b3e6c4',
  shadow:      '0 2px 12px rgba(0,0,0,0.06)',
  font:        FONT,
};

// ─── Helper: format timestamp ────────────────────────────────────────
function fmtTs(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-GB', {
    day:    '2-digit',
    month:  'short',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

// ─── Avatar initials ─────────────────────────────────────────────────
function Avatar({ name, email }) {
  const initials = (name || email || '?').slice(0, 2).toUpperCase();
  const colors   = [BRAND.secondary, BRAND.primaryDark, '#d97706', '#7c3aed', '#dc2626', '#0891b2', '#9333ea'];
  const hash     = [...(name || email || '')].reduce((a, c) => a + c.charCodeAt(0), 0);
  const bg       = colors[hash % colors.length];
  return (
    <div style={{
      width: 36, height: 36, borderRadius: '50%',
      background: bg, color: BRAND.white,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 700, flexShrink: 0,
    }}>{initials}</div>
  );
}

// ─── Module Badge ────────────────────────────────────────────────────
function ModuleBadge({ mod }) {
  return (
    <span style={{
      display:      'inline-block',
      width:        'fit-content',
      maxWidth:     '100%',
      fontSize:     11,
      fontWeight:   600,
      color:        BRAND.secondary,
      background:   BRAND.secondaryLight,
      border:       `1px solid #bfdbfe`,
      borderRadius: 6,
      padding:      '3px 10px',
      whiteSpace:   'nowrap',
      overflow:     'hidden',
      textOverflow: 'ellipsis',
    }}>{mod || '—'}</span>
  );
}

// ───────────────────────────────────────────────────────────────────
export default function AccessLogs({ userData }) {

  const navigate = useNavigate();
  const userId = userData?.['cognito:username'];
  const { profile, user } = useAuth();
  const role   = profile?.cognitogroups || user?.['cognito:groups'] || [];
  const orgId  = profile?.orgId || '';
  const region = sessionStorage.getItem('userRegion') || 'AP';

  const isAdmin = role.includes('Admin');

  // ── State ─────────────────────────────────────────────────────────
  const [logs,         setLogs]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [search,       setSearch]       = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [userFilter,   setUserFilter]   = useState('');
  const [dateRange,    setDateRange]    = useState('7d');
  const [selectedLog,  setSelectedLog]  = useState(null);
  const [allUsers,     setAllUsers]     = useState([]);

  // ── Stats ──────────────────────────────────────────────────────────
  const stats = React.useMemo(() => {
    const now   = Date.now();
    const day   = 86_400_000;
    const today = logs.filter(l => now - l.timestamp < day).length;
    const users = new Set(logs.map(l => l.userId)).size;
    return { total: logs.length, today, users };
  }, [logs]);

  // ── Fetch ──────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!isAdmin || !orgId) return;
    setLoading(true);
    setError('');
    try {
      const fromMap = { '1d': 1, '7d': 7, '30d': 30, 'all': null };
      const days    = fromMap[dateRange];
      const from    = days ? Date.now() - days * 86_400_000 : undefined;

      const data = await fetchActivityLogs({
        orgId, region,
        filters: {
          module:      moduleFilter || undefined,
          actorUserId: userFilter   || undefined,
          from,
        },
      });

      const result = Array.isArray(data) ? data : [];
      setLogs(result);

      if (!userFilter) {
        const seen = new Map();
        result.forEach(l => {
          if (l.userId && !seen.has(l.userId)) {
            seen.set(l.userId, {
              userId:     l.userId,
              actorName:  l.actorName  || '',
              actorEmail: l.actorEmail || '',
              role:       l.actorRole  || '',
            });
          }
        });
        setAllUsers(Array.from(seen.values()));
      }
    } catch (e) {
      setError('Failed to load activity logs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, orgId, region, moduleFilter, userFilter, dateRange]);

  useEffect(() => { load(); }, [load]);

  

  // ── Filtered rows ──────────────────────────────────────────────────
  const visible = logs.filter(l => {
    if (search) {
      const q = search.toLowerCase();
      const match =
        (l.actorEmail  || '').toLowerCase().includes(q) ||
        (l.actorName   || '').toLowerCase().includes(q) ||
        (l.action      || '').toLowerCase().includes(q) ||
        (l.description || '').toLowerCase().includes(q) ||
        (l.module      || '').toLowerCase().includes(q);
      if (!match) return false;
    }
    if (userFilter && l.userId !== userFilter) return false;
    return true;
  });

  // ── Download CSV ───────────────────────────────────────────────────
  const handleDownload = async () => {
    if (!visible.length) return;
    try {
      const clientIp = await getClientIP();

      const headers = [
        'Timestamp', 'User', 'Email', 'Role',
        'Module', 'Action', 'Description', 'Severity',
        'OrgId', 'UserId', 'EventId', 'IP Address',
      ];

      const rows = visible.map(l => [
        fmtTs(l.timestamp),
        l.actorName   || '',
        l.actorEmail  || '',
        l.actorRole   || '',
        l.module      || '',
        l.action      || '',
        (l.description || '').replace(/,/g, ';'),
        l.severity    || '',
        l.orgId       || '',
        l.userId      || '',
        l.eventId     || '',
        l.ipAddress   || '',
      ]);

      const csv  = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `activity-logs-${orgId}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      const effectiveUserData = userData || user || {};
      const resolvedUserId =
        effectiveUserData?.['cognito:username'] ||
        effectiveUserData?.cognitousername      ||
        effectiveUserData?.username             ||
        effectiveUserData?.sub                  ||
        userId || '';

      if (resolvedUserId && orgId) {
        await logActivity({
          userData: {
            ...effectiveUserData,
            'cognito:username': resolvedUserId,
            name:  profile?.name  || effectiveUserData?.name  || resolvedUserId,
            email: profile?.email || effectiveUserData?.email || '',
          },
          orgId,
          region,
          module:      'Access Log',
          action:      'Access Logs Downloaded',
          severity:    'warning',
          description: `Access logs downloaded by ${profile?.email || ''} — ${visible.length} records`,
          ipAddress:   clientIp,
        });

        await new Promise(r => setTimeout(r, 1500));
        await load();
      }
    } catch (err) {
      console.error('❌ Download/log failed:', err);
      setError('Download failed or activity log could not be recorded.');
    }
  };

  // ── Not Admin ──────────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: T.textMuted, fontFamily: T.font }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Access Restricted</div>
        <div style={{ fontSize: 13, marginTop: 6 }}>Only Admins can view activity logs.</div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: T.font, background: T.bg, minHeight: '100%', paddingBottom: 40 }}>

      {/* ── Page Header ──────────────────────────────────────────── */}
      <div style={{ padding: '28px 28px 0' }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: 0, letterSpacing: '-0.3px' }}>
          Access Logs
        </h2>
        <p style={{ fontSize: 13, color: T.textMuted, margin: '4px 0 0' }}>
          All activity across your organisation — <strong style={{ color: T.text }}>{orgId}</strong>
        </p>
      </div>

      {/* ── KPI Cards ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, padding: '20px 28px 0' }}>
        {[
          { label: 'Total Events', value: stats.total, sub: 'All time',      color: T.text },
          { label: 'Today',        value: stats.today, sub: 'Last 24 hours', color: T.text },
          { label: 'Active Users', value: stats.users, sub: 'Unique actors', color: T.text },
        ].map(k => (
          <div key={k.label} style={{
            background: T.card, borderRadius: 10,
            padding: '18px 20px',
            boxShadow: T.shadow,
            border: `1px solid ${T.borderLight}`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 30, fontWeight: 800, color: k.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{k.value}</div>
            <div style={{ fontSize: 11, color: T.textFaint, marginTop: 4 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Filters Bar ───────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '16px 28px 0', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search user, action, description..."
          style={{
            flex: '1 1 260px', padding: '8px 14px',
            border: `1px solid ${T.border}`, borderRadius: 8,
            fontSize: 13, color: T.text, background: T.card, outline: 'none',
          }}
        />

        <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} style={selectStyle}>
          <option value="">All Modules</option>
          {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        <div style={{ position: 'relative' }}>
          <select
            value={userFilter}
            onChange={e => setUserFilter(e.target.value)}
            style={{ ...selectStyle, minWidth: 160, appearance: 'none', paddingRight: 28 }}
          >
            <option value="">All Users</option>
            {allUsers.map(u => (
              <option key={u.userId} value={u.userId}>
                {u.actorName
                  ? `${u.actorName}${u.role ? ` (${u.role.toLowerCase()})` : ''}`
                  : u.actorEmail}
              </option>
            ))}
          </select>
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: BRAND.gray400, fontSize: 11 }}>▼</span>
        </div>

        <select value={dateRange} onChange={e => setDateRange(e.target.value)} style={selectStyle}>
          <option value="1d">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="all">All time</option>
        </select>

        <button
          onClick={() => { setModuleFilter(''); setUserFilter(''); setSearch(''); setDateRange('7d'); }}
          style={{ ...btnStyle, background: T.card, color: T.textMuted, border: `1px solid ${T.border}` }}>
          ✕ Clear
        </button>

        <button onClick={load}
          style={{ ...btnStyle, background: T.brandLight, color: BRAND.primaryDark, border: `1px solid ${T.brandMid}` }}>
          ↻ Refresh
        </button>

        <button
          onClick={handleDownload}
          disabled={!visible.length}
          style={{
            ...btnStyle,
            background: BRAND.secondaryLight,
            color: BRAND.secondaryDark,
            border: `1px solid #bfdbfe`,
            opacity: visible.length ? 1 : 0.45,
            marginLeft: 'auto',
          }}>
          ⬇ Download CSV
        </button>

       
      </div>

      {/* ── Activity Feed Table ────────────────────────────────────── */}
      <div style={{ margin: '16px 28px 0', background: T.card, borderRadius: 12, border: `1px solid ${T.border}`, boxShadow: T.shadow, overflow: 'hidden' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${T.border}` }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Activity Feed</span>
          <span style={{ fontSize: 12, color: T.textMuted, background: T.borderLight, borderRadius: 20, padding: '2px 10px', fontWeight: 600 }}>
            {visible.length} events
          </span>
        </div>

        {/* Table Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '150px 220px 130px 160px 1fr 140px 80px', padding: '8px 20px', background: BRAND.gray50, borderBottom: `1px solid ${T.border}` }}>
          {['TIMESTAMP', 'USER', 'MODULE', 'ACTION', 'DESCRIPTION', 'IP ADDRESS', ''].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: T.textFaint, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: T.textMuted }}>
            <div style={{ display: 'inline-block', width: 22, height: 22, border: `3px solid ${T.brandMid}`, borderTopColor: BRAND.primaryDark, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <div style={{ marginTop: 10, fontSize: 13 }}>Loading activity logs…</div>
          </div>
        ) : error ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#DC2626', fontSize: 13 }}>{error}</div>
        ) : visible.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: T.textMuted }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>📋</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>No activity logs found</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Activities performed by org users will appear here.</div>
          </div>
        ) : (
          <div style={{ maxHeight: 560, overflowY: 'auto' }}>
            {visible.map((log, i) => (
              <div
                key={log.eventId || i}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '150px 220px 130px 160px 1fr 140px 80px',
                  alignItems: 'center',
                  padding: '10px 20px',
                  borderBottom: i < visible.length - 1 ? `1px solid ${T.borderLight}` : 'none',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = BRAND.gray50}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {/* Timestamp */}
                <div style={{ fontSize: 11, color: T.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtTs(log.timestamp)}
                </div>

                {/* User */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar name={log.actorName} email={log.actorEmail} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{log.actorName || log.userId || '—'}</div>
                    <div style={{ fontSize: 10, color: T.textFaint }}>{log.actorEmail || ''}</div>
                  </div>
                </div>

                {/* Module — flex so badge shrink-wraps to text width */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <ModuleBadge mod={log.module} />
                </div>

                {/* Action */}
                <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{log.action || '—'}</div>

                {/* Description */}
                <div style={{ fontSize: 12, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.description || '—'}
                </div>

                {/* IP Address */}
                <div style={{ fontSize: 11, color: T.textMuted, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.ipAddress || '—'}
                </div>

                {/* Details button */}
                <button onClick={() => setSelectedLog(log)} style={{
                  fontSize: 11, padding: '4px 10px', borderRadius: 6,
                  border: `1px solid ${BRAND.primaryDark}`,
                  background: T.brandLight,
                  color: BRAND.primaryDark,
                  cursor: 'pointer', fontWeight: 600,
                }}>
                  ⓘ Details
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Event Details Modal ────────────────────────────────────── */}
      {selectedLog && (
        <div onClick={() => setSelectedLog(null)} style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: BRAND.white, borderRadius: 14,
            width: 520, maxWidth: '95vw', maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            fontFamily: T.font,
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: `1px solid ${T.border}` }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: T.text }}>Event Details</span>
              <button onClick={() => setSelectedLog(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: T.textMuted, padding: '4px 8px', borderRadius: 6 }}>✕</button>
            </div>

            {/* Body */}
            <div style={{ padding: '8px 24px' }}>
              {[
                { label: 'Timestamp',    value: fmtTs(selectedLog.timestamp) },
                { label: 'User',         value: selectedLog.actorName || '—', sub: selectedLog.actorEmail },
                { label: 'Module',       value: <ModuleBadge mod={selectedLog.module} /> },
                { label: 'Action',       value: <strong>{selectedLog.action || '—'}</strong> },
                { label: 'Description',  value: selectedLog.description || '—' },
                { label: 'Organisation', value: selectedLog.orgId,   mono: true },
                { label: 'User ID',      value: selectedLog.userId,  mono: true },
                { label: 'Event ID',     value: selectedLog.eventId, mono: true },
                { label: 'IP Address',   value: selectedLog.ipAddress || '—', mono: true },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: `1px solid ${T.borderLight}`, gap: 16 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.textFaint, textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 110, paddingTop: 2 }}>
                    {row.label}
                  </span>
                  <span style={{ fontSize: 13, color: T.text, textAlign: 'right', fontFamily: row.mono ? 'monospace' : T.font, wordBreak: 'break-all' }}>
                    {row.value}
                    {row.sub && <div style={{ fontSize: 11, color: T.textFaint }}>{row.sub}</div>}
                  </span>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setSelectedLog(null)} style={{
                padding: '8px 22px',
                background: BRAND.primary,
                color: BRAND.gray900,
                border: 'none', borderRadius: 8,
                cursor: 'pointer', fontSize: 14, fontWeight: 700,
              }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${BRAND.gray300}; border-radius: 4px; }
      `}</style>
    </div>
  );
}

const selectStyle = {
  padding: '8px 12px',
  border: `1px solid ${BRAND.gray200}`,
  borderRadius: 8, fontSize: 13,
  color: BRAND.gray900,
  background: BRAND.white,
  cursor: 'pointer', outline: 'none',
};

const btnStyle = {
  padding: '8px 14px', borderRadius: 8,
  fontSize: 13, fontWeight: 600,
  cursor: 'pointer', border: 'none',
  transition: 'all 0.15s',
};